/**
 * The three program memberships the page names.
 *
 * Every field the owner has not supplied evidence for is `null`, and the
 * components leave a `null` out rather than printing a placeholder — so the
 * page can ship before the paperwork arrives and fills in as it does. The
 * prerender fails the build if a bracketed placeholder ever reaches the HTML.
 *
 * Wording rules (docs/DESIGN.md, "Claims"):
 * - NVIDIA: "Member of NVIDIA Inception" only. Never "NVIDIA Partner" — Inception
 *   is not the NVIDIA Partner Network. The NVIDIA logo appears only inside the
 *   official Inception Program lockup the owner supplied (2026-10-03).
 * - Alibaba Cloud: "Alibaba Cloud Partner for Hybrid & Cloud Solutions" (owner's
 *   wording, 2026-10-03).
 * - The Qwen Ambassador title belongs to the founder personally, not the company.
 */

export type CredentialId = 'qwen-ambassador' | 'alibaba-partner' | 'nvidia-inception';

export interface Credential {
  id: CredentialId;
  /** Short form for the hero strip. */
  short: string;
  /** Full sentence for the body copy. */
  long: string;
  /** Who holds it. */
  holder: 'company' | 'founder';
  /** e.g. "May 2026" — appended as "(since …)" when set. */
  since: string | null;
  /** Program tier, e.g. a partner level — appended when set. */
  tier: string | null;
  /** Public page that substantiates it — linked when set. */
  sourceUrl: string | null;
  /** Official artwork, when the owner has supplied it. `says` means the
   *  artwork itself carries the name (a full lockup), so the text is not
   *  repeated beside it; the text becomes its alt. `className` sizes it in the
   *  hero line. */
  badge: { src: string; width: number; height: number; says: boolean; className: string } | null;
}

export const CREDENTIALS: readonly Credential[] = [
  {
    id: 'qwen-ambassador',
    short: "Led by Thailand's first and only Qwen Ambassador",
    long: "Our founder is Thailand's first and only Qwen Ambassador",
    holder: 'founder',
    since: null,
    tier: null,
    sourceUrl: null,
    badge: null,
  },
  {
    id: 'alibaba-partner',
    short: 'Alibaba Cloud Partner for Hybrid & Cloud Solutions',
    long: 'Official Alibaba Cloud Partner for Hybrid & Cloud Solutions',
    holder: 'company',
    since: null,
    tier: null,
    sourceUrl: null,
    badge: { src: '/img/alibaba-cloud-mark.webp', width: 152, height: 96, says: false, className: 'h-5' },
  },
  {
    id: 'nvidia-inception',
    short: 'Member of NVIDIA Inception',
    long: 'AGICAFET is a member of NVIDIA Inception',
    holder: 'company',
    since: null,
    tier: null,
    sourceUrl: null,
    badge: { src: '/img/nvidia-inception.webp', width: 560, height: 190, says: true, className: 'h-9' },
  },
];

export function credential(id: CredentialId): Credential {
  const c = CREDENTIALS.find((x) => x.id === id);
  if (!c) throw new Error(`unknown credential ${id}`);
  return c;
}

/** "(since May 2026)" / "(Gold, since May 2026)" / "" — only what is known. */
export function qualifier(c: Credential): string {
  const parts = [c.tier, c.since && `since ${c.since}`].filter(Boolean);
  return parts.length ? ` (${parts.join(', ')})` : '';
}
